import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, Navigate, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, ShieldAlert } from 'lucide-react'
import {
  SidebarProvider,
  SidebarInset,
} from '@/components/ui/sidebar'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { AppSidebar } from '@/components/layout/app-sidebar'
import { AppHeader } from '@/components/layout/app-header'
import { WorkspacePortalProvider } from '@/contexts/workspace-portal-context'
import { EditingLanguageProvider } from '@/contexts/editing-language-context'
import { MarketingAdminBanner } from '@/components/marketing/marketing-admin-banner'
import { authApi } from '@/services/auth'
import { orgTreeApi, orgServiceInstanceLightQueryKey } from '@/services/org-tree'
import { useAuthStore, getBookmarkedPostKey } from '@/stores/auth-store'
import { buildPostSelectionPath } from '@/lib/auth-redirect'
import { buildAuthThemeStyle } from '@/lib/auth-theme'
import { evaluateRouteAccess, getCurrentRoleScopeLabel } from '@/lib/route-access'
import { useEffectivePostAccess } from '@/hooks/use-effective-post-access'
import { useFcmNotifications } from '@/hooks/use-fcm-notifications'
import { useNodeLinkedServiceInstances } from '@/hooks/use-node-linked-service-instances'
import { buildNodeServicePermissionOptionsFromPosts } from '@/lib/node-role-services'
import {
  buildServiceVariantHomePath,
  getServiceInstanceIdFromSearch,
  resolveActiveServiceInstanceId,
  resolveEventScopedServiceInstanceId,
  resolveFundraisingScopedServiceInstanceId,
  resolveWorkspaceActiveServiceInstanceId,
  withServiceInstanceId,
} from '@/lib/service-instance'

function isApprovedServiceStatus(status: unknown) {
  if (status == null) {
    return true
  }

  const normalized = String(status).trim().toLowerCase()
  return normalized === '1' || normalized === 'approved' || normalized === 'active'
}

function isPendingRegistrationStatus(status: unknown) {
  const normalized = String(status ?? '').trim().toLowerCase()
  return normalized === '1' || normalized === 'pending'
}

function hasCapabilityLikePayload(value: unknown) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false
  }

  return Object.values(value as Record<string, unknown>).some((entry) => {
    if (entry === null || entry === undefined) {
      return false
    }
    const t = typeof entry
    return t === 'boolean' || t === 'number' || t === 'string'
  })
}

const STRICT_CAPABILITY_GUARDS = String(
  import.meta.env.VITE_STRICT_CAPABILITY_GUARDS ?? '',
).trim().toLowerCase() === 'true'

export default function ProtectedLayout() {
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const accessToken = useAuthStore((state) => state.accessToken)
  const sessionId = useAuthStore((state) => state.sessionId)
  const storedServiceInstanceId = useAuthStore((state) => state.serviceInstanceId)
  const setServiceInstanceId = useAuthStore((state) => state.setServiceInstanceId)
  const currentPost = useAuthStore((state) => state.currentPost)
  const availablePosts = useAuthStore((state) => state.availablePosts)
  const user = useAuthStore((state) => state.user)
  const currentBranch = useAuthStore((state) => state.currentBranch)
  const availableBranches = useAuthStore((state) => state.availableBranches)
  const setBranch = useAuthStore((state) => state.setBranch)
  const setBranches = useAuthStore((state) => state.setBranches)
  const serviceInstanceIdFromUrl = getServiceInstanceIdFromSearch(location.search)
  const explicitNodeIdFromUrl = String(searchParams.get('node_id') ?? '').trim()
  const baseServiceInstanceId = resolveActiveServiceInstanceId({
    search: location.search,
    post: currentPost,
    currentBranch,
    availableBranches,
    storedServiceInstanceId,
    token: accessToken,
  })
  useFcmNotifications(baseServiceInstanceId, Boolean(accessToken))
  const workspaceNodeIdFromPost = String(currentPost?.node_id ?? '').trim()
  const isBranchWorkspaceRoute = location.pathname === '/org/branch'
  const postDerivedWorkspaceOptions = buildNodeServicePermissionOptionsFromPosts(
    availablePosts,
    workspaceNodeIdFromPost,
  )
  const nodeLinkedServiceInstances = useNodeLinkedServiceInstances({
    nodeId: workspaceNodeIdFromPost,
    nodeIds: explicitNodeIdFromUrl ? [workspaceNodeIdFromPost, explicitNodeIdFromUrl] : [workspaceNodeIdFromPost],
    activeServiceInstanceId: baseServiceInstanceId,
    enabled: Boolean((workspaceNodeIdFromPost || explicitNodeIdFromUrl) && currentPost),
    supplementalOptions: postDerivedWorkspaceOptions,
  })
  const resolvedServiceInstanceId = useMemo(
    () => {
      if (location.pathname.startsWith('/fundraising')) {
        return resolveFundraisingScopedServiceInstanceId({
          search: location.search,
          post: currentPost,
          availablePosts,
          storedServiceInstanceId,
        }) || resolveWorkspaceActiveServiceInstanceId({
          search: location.search,
          post: currentPost,
          currentBranch,
          availableBranches,
          storedServiceInstanceId,
          token: accessToken,
          nodeInstanceIds: nodeLinkedServiceInstances.serviceOptions.map((option) => option.instanceId),
        }) || baseServiceInstanceId
      }

      // Events APIs require IsValidPost against the Events in-app SI. A leftover
      // branch/tenant SI in the URL causes 401 "Invalid Id or Session Token".
      if (location.pathname.startsWith('/events')) {
        return resolveEventScopedServiceInstanceId({
          search: location.search,
          post: currentPost,
          availablePosts,
          storedServiceInstanceId,
        }) || resolveWorkspaceActiveServiceInstanceId({
          search: location.search,
          post: currentPost,
          currentBranch,
          availableBranches,
          storedServiceInstanceId,
          token: accessToken,
          nodeInstanceIds: nodeLinkedServiceInstances.serviceOptions.map((option) => option.instanceId),
        }) || baseServiceInstanceId
      }

      return resolveWorkspaceActiveServiceInstanceId({
        search: location.search,
        post: currentPost,
        currentBranch,
        availableBranches,
        storedServiceInstanceId,
        token: accessToken,
        nodeInstanceIds: nodeLinkedServiceInstances.serviceOptions.map((option) => option.instanceId),
      }) || baseServiceInstanceId
    },
    [
      availableBranches,
      availablePosts,
      accessToken,
      baseServiceInstanceId,
      currentBranch,
      currentPost,
      location.pathname,
      location.search,
      nodeLinkedServiceInstances.serviceOptions,
      storedServiceInstanceId,
    ],
  )
  const isPostGate = location.pathname === '/posts'
  const isProfileRoute = location.pathname === '/profile'
  const isPendingUser = isPendingRegistrationStatus(user?.registration_status)
  const hasSelectedRole = Boolean(currentPost)
  const authRefreshKeyRef = useRef<string | null>(null)
  const branchListKeyRef = useRef<string | null>(null)
  const roleHydrationInFlightRef = useRef(false)
  const [isHydratingCurrentRoleCapabilities, setIsHydratingCurrentRoleCapabilities] = useState(false)
  const [hasSyncGateDelayElapsed, setHasSyncGateDelayElapsed] = useState(false)
  const { effectivePost } = useEffectivePostAccess(currentPost)
  const routeAccess = evaluateRouteAccess(location.pathname, effectivePost ?? currentPost)
  const currentRoleScopeLabel = getCurrentRoleScopeLabel(currentPost)
  const preferredDashboardHref = buildServiceVariantHomePath(currentPost, resolvedServiceInstanceId)
  const preferredDashboardPath = preferredDashboardHref.split('?')[0] || preferredDashboardHref
  const dashboardFallbackHref = evaluateRouteAccess(preferredDashboardPath, currentPost).authorized
    ? preferredDashboardHref
    : withServiceInstanceId('/dashboard', resolvedServiceInstanceId)

  const serviceInstanceQuery = useQuery({
    queryKey: orgServiceInstanceLightQueryKey(String(resolvedServiceInstanceId)),
    queryFn: () => orgTreeApi.resolvePublicSignupServiceInstance(String(resolvedServiceInstanceId)),
    enabled: Boolean(resolvedServiceInstanceId) && !isBranchWorkspaceRoute,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
  const adminThemeStyle = useMemo(
    () => buildAuthThemeStyle(serviceInstanceQuery.data?.primary_color),
    [serviceInstanceQuery.data?.primary_color],
  )

  const isRestrictedServiceInstance = !isApprovedServiceStatus(serviceInstanceQuery.data?.status)
  // Prefer stable role+SI identity over encrypted post.id so SI/auth refresh
  // does not remount the shell or re-fire UserPostList hydrate on id churn.
  const currentPostIdentityKey = getBookmarkedPostKey(currentPost)
  const currentPostIdKey = currentPost?.id == null ? '' : String(currentPost.id)
  const roleCapabilitiesReady = hasCapabilityLikePayload(currentPost?.capabilities)
  const shouldGateForRoleCapabilities = Boolean(
    STRICT_CAPABILITY_GUARDS
    &&
    !isPostGate
    && !isProfileRoute
    && hasSelectedRole
    && currentPostIdentityKey
    && !roleCapabilitiesReady
  )
  const shouldShowRoleSyncGate = shouldGateForRoleCapabilities || (
    STRICT_CAPABILITY_GUARDS
    && isHydratingCurrentRoleCapabilities
    && !roleCapabilitiesReady
  )

  useEffect(() => {
    if (!shouldShowRoleSyncGate) {
      setHasSyncGateDelayElapsed(false)
      return undefined
    }

    const timeoutId = window.setTimeout(() => {
      setHasSyncGateDelayElapsed(true)
    }, 300)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [shouldShowRoleSyncGate])

  useEffect(() => {
    if (resolvedServiceInstanceId && resolvedServiceInstanceId !== storedServiceInstanceId) {
      setServiceInstanceId(resolvedServiceInstanceId)
    }
  }, [resolvedServiceInstanceId, setServiceInstanceId, storedServiceInstanceId])

  // Keep fundraising / events routes pinned to their in-app SI (not branch).
  useEffect(() => {
    const isPinnedInAppRoute = location.pathname.startsWith('/fundraising')
      || location.pathname.startsWith('/events')
    if (!isPinnedInAppRoute || !resolvedServiceInstanceId) {
      return
    }
    const urlSi = getServiceInstanceIdFromSearch(location.search)
    if (urlSi === resolvedServiceInstanceId) {
      return
    }
    navigate(
      withServiceInstanceId(`${location.pathname}${location.search}`, resolvedServiceInstanceId),
      { replace: true },
    )
  }, [location.pathname, location.search, navigate, resolvedServiceInstanceId])

  useEffect(() => {
    if (!accessToken || !sessionId) {
      authRefreshKeyRef.current = null
      return
    }

    // Refresh auth once per session — not on every in-app SI switch.
    // Posts are role-scoped across sibling in-apps; SI change alone should not
    // re-hit getByToken (which previously cascaded into UserPostList hydrate).
    const nextKey = String(sessionId)
    if (authRefreshKeyRef.current === nextKey) {
      return
    }

    authRefreshKeyRef.current = nextKey

    void authApi.refreshAuthState().catch(() => {
      authRefreshKeyRef.current = null
    })
  }, [accessToken, sessionId])

  useEffect(() => {
    let isCancelled = false

    if (!accessToken) {
      branchListKeyRef.current = null
      return undefined
    }

    // Branch directory is org/session scoped — avoid refetching on every in-app SI change.
    const nextKey = String(sessionId || accessToken)
    if (branchListKeyRef.current === nextKey) {
      return undefined
    }
    branchListKeyRef.current = nextKey

    void authApi.getServiceInstances()
      .then((branches) => {
        if (isCancelled) {
          return
        }

        setBranches(branches)
        const matchingBranch = branches.find((branch) => (
          String(branch.id) === String(resolvedServiceInstanceId)
          || branch.instanceId === resolvedServiceInstanceId
          || branch.code === resolvedServiceInstanceId
        ))

        if (!matchingBranch) {
          return
        }

        const storeBranch = useAuthStore.getState().currentBranch
        if (String(storeBranch?.id ?? '') !== String(matchingBranch.id)) {
          setBranch(matchingBranch)
        }
      })
      .catch(() => {
        if (!isCancelled) {
          branchListKeyRef.current = null
        }
      })

    return () => {
      isCancelled = true
    }
  }, [accessToken, resolvedServiceInstanceId, sessionId, setBranch, setBranches])

  // When switching in-app SI, re-point currentBranch from the cached list (no network).
  useEffect(() => {
    if (!resolvedServiceInstanceId) {
      return
    }

    const branches = useAuthStore.getState().availableBranches
    if (!branches.length) {
      return
    }

    const matchingBranch = branches.find((branch) => (
      String(branch.id) === String(resolvedServiceInstanceId)
      || branch.instanceId === resolvedServiceInstanceId
      || branch.code === resolvedServiceInstanceId
    ))

    if (!matchingBranch) {
      return
    }

    const storeBranch = useAuthStore.getState().currentBranch
    if (String(storeBranch?.id ?? '') !== String(matchingBranch.id)) {
      setBranch(matchingBranch)
    }
  }, [resolvedServiceInstanceId, setBranch])

  useEffect(() => {
    if (!currentPostIdentityKey || !currentPostIdKey || isPostGate || isProfileRoute) {
      return
    }

    // Capabilities already present for this role+SI — avoid redundant UserPostList.
    if (roleCapabilitiesReady) {
      return
    }

    if (roleHydrationInFlightRef.current) {
      return
    }

    roleHydrationInFlightRef.current = true
    setIsHydratingCurrentRoleCapabilities(true)

    void authApi.hydrateSelectedRoleCapabilities(currentPostIdKey)
      .catch(() => undefined)
      .finally(() => {
        roleHydrationInFlightRef.current = false
        setIsHydratingCurrentRoleCapabilities(false)
      })
  }, [
    currentPostIdentityKey,
    currentPostIdKey,
    isPostGate,
    isProfileRoute,
    roleCapabilitiesReady,
  ])

  useEffect(() => {
    if (!currentPostIdentityKey || !currentPostIdKey || isPostGate || isProfileRoute) {
      return undefined
    }

    const refreshCapabilities = () => {
      if (roleHydrationInFlightRef.current || document.visibilityState !== 'visible') {
        return
      }
      // Don't thrash UserPostList on every tab focus when caps are already loaded.
      if (hasCapabilityLikePayload(useAuthStore.getState().currentPost?.capabilities)) {
        return
      }

      roleHydrationInFlightRef.current = true
      void authApi.hydrateSelectedRoleCapabilities(currentPostIdKey)
        .catch(() => undefined)
        .finally(() => {
          roleHydrationInFlightRef.current = false
        })
    }

    window.addEventListener('focus', refreshCapabilities)
    document.addEventListener('visibilitychange', refreshCapabilities)

    return () => {
      window.removeEventListener('focus', refreshCapabilities)
      document.removeEventListener('visibilitychange', refreshCapabilities)
    }
  }, [currentPostIdentityKey, currentPostIdKey, isPostGate, isProfileRoute])

  const shouldDeferServiceInstanceRedirect = Boolean(
    serviceInstanceIdFromUrl
    && nodeLinkedServiceInstances.isLoading
    && workspaceNodeIdFromPost,
  )

  if (
    !shouldDeferServiceInstanceRedirect
    && resolvedServiceInstanceId
    && serviceInstanceIdFromUrl
    && serviceInstanceIdFromUrl !== resolvedServiceInstanceId
  ) {
    return <Navigate to={withServiceInstanceId(`${location.pathname}${location.search}`, resolvedServiceInstanceId)} replace />
  }

  if (!isPostGate && !isProfileRoute && availablePosts.length > 0 && !hasSelectedRole) {
    return <Navigate to={buildPostSelectionPath(resolvedServiceInstanceId, `${location.pathname}${location.search}`)} replace />
  }

  if (!isPostGate && !isProfileRoute && availablePosts.length === 0 && !hasSelectedRole) {
    return <Navigate to={buildPostSelectionPath(resolvedServiceInstanceId, `${location.pathname}${location.search}`)} replace />
  }

  if (isProfileRoute && isPendingUser && searchParams.get('tab') === 'settings') {
    return <Navigate to={withServiceInstanceId('/profile?tab=personal', resolvedServiceInstanceId)} replace />
  }

  if (!isPostGate && !isProfileRoute && resolvedServiceInstanceId && !serviceInstanceQuery.isLoading && isRestrictedServiceInstance) {
    return (
      <Navigate
        to={buildPostSelectionPath(resolvedServiceInstanceId, `${location.pathname}${location.search}`)}
        replace
      />
    )
  }

  if (isPostGate) {
    return (
      <div className="min-h-screen bg-transparent" style={adminThemeStyle}>
        <AppHeader />
        <main className="px-4 pb-8 pt-4 sm:px-6 lg:px-8">
          <div className="mx-auto w-full max-w-[1440px]">
            <Outlet />
          </div>
        </main>
      </div>
    )
  }

  if (!hasSelectedRole) {
    return (
      <main className="px-4 pb-8 pt-4 sm:px-6 lg:px-8" style={adminThemeStyle}>
        <div className="mx-auto w-full max-w-[1440px]">
          <Outlet />
        </div>
      </main>
    )
  }

  if (shouldShowRoleSyncGate && hasSyncGateDelayElapsed) {
    return (
      <main className="px-4 pb-8 pt-4 sm:px-6 lg:px-8" style={adminThemeStyle}>
        <div className="mx-auto w-full max-w-[1440px]">
          <Card className="rounded-[24px] border-border/70">
            <CardHeader>
              <CardTitle className="text-lg">Syncing role access</CardTitle>
              <CardDescription>
                Loading workspace access for your selected role.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                <div className="h-full w-1/3 animate-pulse rounded-full bg-primary" />
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    )
  }

  const unauthorizedContent = !isPostGate && !isProfileRoute && !routeAccess.authorized ? (
    <Card className="rounded-[32px] border-amber-200 bg-amber-50/70 shadow-sm">
      <CardHeader className="space-y-3 p-6 sm:p-8">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-100 text-amber-700">
            <ShieldAlert className="h-5 w-5" />
          </div>
          <div className="space-y-2">
            <Badge variant="outline" className="w-fit rounded-md border-amber-300 bg-white/70 text-amber-800">
              Access restricted
            </Badge>
            <CardTitle className="text-2xl text-foreground">Not authorized for this page</CardTitle>
            <CardDescription className="max-w-2xl text-sm leading-6 text-muted-foreground sm:text-base">
              The current role does not belong to the feature or service scope required for this page.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5 p-6 pt-0 sm:p-8 sm:pt-0">
        <div className="grid gap-4 md:grid-cols-3">
          <div className="rounded-2xl border border-amber-200 bg-white/80 p-4">
            <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Feature</p>
            <p className="mt-2 text-base font-semibold text-foreground">{routeAccess.feature || 'Protected page'}</p>
          </div>
          <div className="rounded-2xl border border-amber-200 bg-white/80 p-4">
            <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Required service scope</p>
            <p className="mt-2 text-base font-semibold text-foreground">{routeAccess.requiredScopeLabel || 'Current role scope'}</p>
          </div>
          <div className="rounded-2xl border border-amber-200 bg-white/80 p-4">
            <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Current role scope</p>
            <p className="mt-2 text-base font-semibold text-foreground">{currentRoleScopeLabel}</p>
          </div>
        </div>

        <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-white/80 p-4 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            {routeAccess.reason === 'scope'
              ? 'Switch to a role in the correct service scope to open this page.'
              : 'This page is mapped to a feature your current role cannot access.'}
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button asChild>
            <Link to={buildPostSelectionPath(resolvedServiceInstanceId, null)}>
              Switch role
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link to={dashboardFallbackHref}>
              Go to dashboard
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  ) : null

  return (
    // Keep editing-language state outside the keyed sidebar so page/role remounts
    // do not reset the selected content language.
    <EditingLanguageProvider serviceInstanceId={resolvedServiceInstanceId || undefined}>
      <SidebarProvider defaultOpen key={currentPostIdentityKey || 'no-post'} style={adminThemeStyle}>
        <AppSidebar />
        <SidebarInset className="bg-transparent">
          <AppHeader />
          <main className="relative flex-1 overflow-auto px-4 pb-8 pt-4 sm:px-6 lg:px-8">
            <div
              className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-64"
              style={{
                background: 'radial-gradient(circle at top, color-mix(in oklab, var(--primary) 14%, transparent), transparent 55%)',
              }}
            />
            <div className="mx-auto w-full max-w-[1440px]">
              {unauthorizedContent || (
                <WorkspacePortalProvider>
                  <MarketingAdminBanner />
                  <Outlet />
                </WorkspacePortalProvider>
              )}
            </div>
          </main>
        </SidebarInset>
      </SidebarProvider>
    </EditingLanguageProvider>
  )
}
